import React, { useMemo, useState, useEffect } from "react";

interface RowData {
  id?: number;
  club_id?: number;
  logo_url?: string;
  rank_change?: number | string | null;
  rank: number;
  previous_rank?: number | string | null;
  team: string;
  stadium_name: string;
  titan_index: number;
  current_league: string;
  league_position: number;
  top_flight_titles: number;
  european_titles: number;
  fa_cup_titles: number;
  league_cup_titles: number;
  financial_value: string;
  average_attendance?: number;
  seasons_top_division?: number;
  trophies_last_5_years?: number;
  avg_position_last_5_years?: number;
  facebook_followers?: number;
}

interface DataTableProps {
  data: RowData[];
  loading?: boolean;
  currentPage: number;
  totalPage: number;
  onPageChange: (page: number) => void;
}

interface SortConfig {
  key?: keyof RowData;
  direction: "asc" | "desc" | "";
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  loading,
  currentPage,
  totalPage,
  onPageChange,
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: undefined,
    direction: "",
  });

  const [search] = useState("");
  const rowsPerPage = 100;

  /* ================= FILTER ================= */
  const filteredData = useMemo(() => {
    return data.filter((row) =>
      Object.values(row).some((val) =>
        String(val).toLowerCase().includes(search.toLowerCase()),
      ),
    );
  }, [data, search]);

  /* ================= SORT ================= */
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;

    return [...filteredData].sort((a, b) => {
      const key = sortConfig.key!;
      const aRaw = a[key];
      const bRaw = b[key];

      // Coerce to number if both values are numeric (handles string-typed numeric fields)
      const aNum = Number(aRaw);
      const bNum = Number(bRaw);
      const bothNumeric =
        (aRaw !== null && aRaw !== undefined && aRaw !== "" && !isNaN(aNum)) &&
        (bRaw !== null && bRaw !== undefined && bRaw !== "" && !isNaN(bNum));

      if (bothNumeric) {
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      return sortConfig.direction === "asc"
        ? String(aRaw ?? "").localeCompare(String(bRaw ?? ""))
        : String(bRaw ?? "").localeCompare(String(aRaw ?? ""));
    });
  }, [filteredData, sortConfig]);

  /* ================= RESET PAGE ON SEARCH ================= */
  useEffect(() => {
    onPageChange(1);
  }, [search]);

  /* ================= SORT HANDLER ================= */
  const handleSort = (key: keyof RowData) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        if (prev.direction === "asc") return { key, direction: "desc" };
        if (prev.direction === "desc") return { key: undefined, direction: "" };
      }
      return { key, direction: "asc" };
    });
  };

  /* ================= COLUMNS ================= */
  const columns: { key: keyof RowData; label: string }[] = [
    { key: "rank", label: "Rank" },
    { key: "team", label: "Team Name" },
    { key: "titan_index", label: "Titan Index" },
    { key: "current_league", label: "Current League" },
    { key: "league_position", label: "League Position" },
    { key: "top_flight_titles", label: "Top Flight English Titles" },
    { key: "european_titles", label: "Top Flight European Titles" },
    { key: "fa_cup_titles", label: "FA Cup Titles" },
    { key: "league_cup_titles", label: "League Cup Titles" },
    { key: "financial_value", label: "Financial Value (£)" },
    { key: "average_attendance", label: "Average Attendance (K)" },
    { key: "seasons_top_division", label: "Seasons in Top Division" },
    { key: "trophies_last_5_years", label: "Trophies in Last 5 Years" },
    {
      key: "avg_position_last_5_years",
      label: "Avg. Position in Last 5 Years",
    },
    { key: "facebook_followers", label: "Facebook Followers" },
  ];

  /* ================= FORMATTERS ================= */
  const formatFinancialValue = (value: number) => {
    if (isNaN(value)) return "--";
    return `${(value / 1_000_000).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} M`;
  };

  const formatSmartNumber = (value: any) => {
    if (value === null || value === undefined || value === "") return "0";
    const num = Number(value);
    if (isNaN(num)) return value;

    return Number.isInteger(num)
      ? num.toLocaleString("en-US")
      : num.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  };

  const getDisplayRankChange = (row: RowData) => {
    const currentRank = Number(row.rank);
    const previousRank = Number(row.previous_rank);

    if (
      Number.isFinite(previousRank) &&
      Number.isFinite(currentRank) &&
      previousRank > 0 &&
      currentRank > 0
    ) {
      // Lower rank number means moving up, so movement is previous - current.
      return previousRank - currentRank;
    }

    const apiRankChange = Number(row.rank_change);
    if (Number.isFinite(apiRankChange)) {
      // Backend sends opposite sign for UI semantics, so invert for display.
      return apiRankChange * -1;
    }

    return 0;
  };

  return (
    <div className="w-full">
      {loading ? (
        <div className="text-center py-6">Loading...</div>
      ) : (
        <div className="bg-darkBlue p-1 rounded-lg">
          <div className="relative overflow-x-auto overflow-y-auto max-h-[80vh] ranktBl">
            <table className="min-w-full bg-white text-sm">
              <thead className="sticky top-0 z-30 bg-darkBlue text-white">
                <tr>
                  {columns.map((col) => {
                    const isActive = sortConfig.key === col.key;

                    return (
                      <th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        className={`cursor-pointer px-2 md:px-4 py-2 text-left whitespace-nowrap border-r border-blue-400 bg-darkBlue ${
                          col.key === "rank"
                            ? "sticky left-0 z-40 bg-darkBlue min-w-[85px] md:min-w-[130px] border-none"
                            : col.key === "team"
                              ? "sticky left-[85px] md:left-[130px] z-30 bg-darkBlue min-w-[150px] md:min-w-[220px]"
                            : ""
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {col.label}

                          {/* STACKED SORT ICONS (LIKE SCREENSHOT) */}
                          <div className="flex flex-col leading-none text-[10px]">
                            <span
                              className={`${
                                isActive && sortConfig.direction === "asc"
                                  ? "text-yellow-400"
                                  : "text-white-600"
                              }`}
                            >
                              ▲
                            </span>
                            <span
                              className={`${
                                isActive && sortConfig.direction === "desc"
                                  ? "text-yellow-400"
                                  : "text-white-600"
                              }`}
                            >
                              ▼
                            </span>
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {sortedData.length ? (
                  sortedData.map((row, rowIndex) => {
                    const rowKey =
                      row.id ??
                      row.club_id ??
                      `${row.team}-${row.current_league}-${row.rank ?? rowIndex}`;

                    return (
                      <tr
                        key={rowKey}
                        className="border-b odd:bg-white even:bg-[#E5F0FF]"
                      >
                        {columns.map((col) => {
                          if (col.key === "rank") {
                            const changeValue = getDisplayRankChange(row);
                            const changeLabel =
                              Number.isFinite(changeValue) && changeValue !== 0
                                ? `${changeValue > 0 ? "+" : ""}${changeValue}`
                                : "—";
                            const changeClass =
                              changeValue > 0
                                ? "text-green-600"
                                : changeValue < 0
                                  ? "text-red-600"
                                  : "text-gray-500";

                            return (
                              <td
                                key={col.key}
                                className="px-2 md:px-4 py-2 text-center rankCell sticky left-0 z-10 min-w-[85px] md:min-w-[130px]"
                                style={{ backgroundColor: "inherit" }}
                              >
                                <div className="flex items-center justify-center gap-1 md:gap-2">
                                  <span className="rankBadge inline-flex items-center justify-center rounded-lg w-8 h-8 md:w-10 md:h-10 text-[#005486] text-xs md:text-sm font-medium">
                                    {formatSmartNumber(row.rank)}
                                  </span>
                                  <span
                                    className={`text-[10px] md:text-xs font-semibold ${changeClass}`}
                                  >
                                    {changeLabel}
                                  </span>
                                </div>
                              </td>
                            );
                          }

                          if (col.key === "team") {
                            return (
                              <td
                                key={col.key}
                                className="px-2 md:px-4 py-2 text-left min-w-[150px] md:min-w-[220px] max-w-[150px] md:max-w-[320px] teamCell sticky left-[85px] md:left-[130px] z-10"
                                style={{ backgroundColor: "inherit" }}
                              >
                                <span className="whitespace-normal break-words leading-tight md:leading-5 text-xs md:text-sm">
                                  {row.team || "--"}
                                </span>
                              </td>
                            );
                          }

                          return (
                            <td key={col.key} className="px-4 py-2 text-center">
                              {col.key === "financial_value" ? (
                                formatFinancialValue(Number(row[col.key]))
                              ) : typeof row[col.key] === "number" ||
                                !isNaN(Number(row[col.key])) ? (
                                <span
                                  className={
                                    col.key === "titan_index" ? "font-semibold" : ""
                                  }
                                >
                                  {formatSmartNumber(row[col.key])}
                                </span>
                              ) : (
                                <span
                                  className={
                                    col.key === "titan_index" ? "font-semibold" : ""
                                  }
                                >
                                  {row[col.key]}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    {/* <td colSpan={columns.length} className="text-center py-4">
                      No Record Found
                    </td> */}
                    <td colSpan={columns.length} className="py-4">
                      <span
                        style={{
                          display: "inline-block",
                          paddingTop: "70px",
                          paddingBottom: "70px",
                          marginLeft: "calc(35vw - 0px)",
                        }}
                      >
                        No Record Found
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
